import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useEditor } from '@grapesjs/react';

interface TemplateLibraryProps {
  isOpen: boolean;
  onClose: () => void;
}

const API_BASE = (import.meta as any).env?.VITE_API_URL || 'http://localhost:4000';

// GrapesJS's CssComposer parses whatever string is handed to setStyle() into
// individual rule models — it isn't a real browser stylesheet, so an
// @import url(...) at-rule (e.g. dreamxTemplate.js's Google Fonts import)
// isn't something it knows how to turn into a rule at all. Passing it
// through anyway risks the parser choking on that line and silently
// dropping every rule that follows it — which is what was leaving DreamX
// template pages completely unstyled even after fixing the getStyle()/
// getCss() bug. Strip @import statements out and load those stylesheets
// for real, via actual <link> tags in the canvas iframe's head, then return
// the import-free CSS for setStyle() to parse normally.
function extractCssImports(css: string): { css: string; importUrls: string[] } {
  const importUrls: string[] = [];
  const stripped = css.replace(/@import\s+url\(['"]?([^'")]+)['"]?\)\s*;?/gi, (_match, url) => {
    importUrls.push(url);
    return '';
  });
  return { css: stripped, importUrls };
}

function injectStylesheetLinks(editor: any, urls: string[]): void {
  try {
    const canvasDoc = editor.Canvas?.getDocument?.();
    if (!canvasDoc || !canvasDoc.head) return;
    urls.forEach((url) => {
      const existing = Array.from(canvasDoc.querySelectorAll('link[rel="stylesheet"]'))
        .some((el: any) => el.href === url);
      if (existing) return;
      const link = canvasDoc.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      canvasDoc.head.appendChild(link);
    });
  } catch (e) {
    console.warn('Could not inject template stylesheet links:', e);
  }
}

// Strips @import statements from `css`, injects them as real <link> tags,
// and returns the import-free CSS ready to hand to editor.setStyle().
function prepareTemplateCss(editor: any, css: string): string {
  const { css: stripped, importUrls } = extractCssImports(css);
  if (importUrls.length) injectStylesheetLinks(editor, importUrls);
  return stripped;
}

// Category display labels
const CATEGORY_LABELS: Record<string, string> = {
  'real-estate': 'Real Estate',
  'core': 'Core Pages',
  'business': 'Business',
  'content': 'Content',
  'portfolio': 'Portfolio',
  'restaurant': 'Restaurant',
  'custom': 'Blank',
  'Full Websites': 'Full Websites',
  'Landing Pages': 'Landing Pages',
  'Property Listings': 'Property Listings',
  'About Pages': 'About Pages',
  'Contact Pages': 'Contact Pages',
  'Basic': 'Blank',
};

// Visual style per backend template ID
const BACKEND_STYLES: Record<string, { bg: string }> = {
  'home':               { bg: 'linear-gradient(135deg, #3b82f6, #06b6d4)' },
  'about':              { bg: 'linear-gradient(135deg, #f59e0b, #f97316)' },
  'contact':            { bg: 'linear-gradient(135deg, #6366f1, #3b82f6)' },
  'services':           { bg: 'linear-gradient(135deg, #10b981, #14b8a6)' },
  'blog':               { bg: 'linear-gradient(135deg, #6f42c1, #9333ea)' },
  'business-landing':   { bg: 'linear-gradient(135deg, #1e3a5f, #2563eb)' },
  'portfolio-showcase': { bg: 'linear-gradient(135deg, #ec4899, #f43f5e)' },
  'restaurant-menu':    { bg: 'linear-gradient(135deg, #ef4444, #f97316)' },
  'eden-real-estate':   { bg: 'linear-gradient(135deg, #667eea, #764ba2)' },
  'homestate-real-estate': { bg: 'linear-gradient(135deg, #11998e, #38ef7d)' },
  'custom':             { bg: 'linear-gradient(135deg, #94a3b8, #64748b)' },
};

// Icon SVGs keyed by template ID
function TemplateIcon({ id }: { id: string }) {
  const iconMap: Record<string, React.ReactNode> = {
    'home': (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    'about': (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    'contact': (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    'services': (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    'blog': (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
      </svg>
    ),
    'business-landing': (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    'portfolio-showcase': (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    'restaurant-menu': (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
      </svg>
    ),
    'eden-real-estate': (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    'homestate-real-estate': (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
      </svg>
    ),
    'custom': (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  };
  return <>{iconMap[id] || iconMap['custom']}</>;
}

// Static local templates (always available, no API needed)
const LOCAL_TEMPLATES = [
  {
    id: 'dreamx-7-page-real-estate',
    name: 'DreamX 7-Page Real Estate Site',
    category: 'Full Websites',
    description: 'Home, Buy, Rent, Sell, Loans, About & Listings — warm cream design with Repliers API hooks',
    badge: '7 Pages',
    bg: 'linear-gradient(135deg, #1F3D2E, #2d5940)',
    source: 'local' as const,
    icon: (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 40 28" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 24 L14 6 L20 16 L26 8 L36 24"/>
        <path strokeLinecap="round" d="M2 24 L38 24"/>
      </svg>
    ),
  },
  {
    id: 'complete-real-estate-website',
    name: 'Complete Real Estate Website',
    category: 'Full Websites',
    description: 'Home, listings, about & contact pages',
    badge: '4 Pages',
    bg: 'linear-gradient(135deg, #7c3aed, #9333ea)',
    source: 'local' as const,
    icon: (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
      </svg>
    ),
  },
  {
    id: 'real-estate-hero-modern',
    name: 'Modern Hero Landing',
    category: 'Landing Pages',
    description: 'Eye-catching hero section for your homepage',
    bg: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
    source: 'local' as const,
    icon: (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    id: 'real-estate-property-grid',
    name: 'Property Grid Layout',
    category: 'Property Listings',
    description: 'Showcase multiple properties in a grid',
    bg: 'linear-gradient(135deg, #10b981, #14b8a6)',
    source: 'local' as const,
    icon: (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
      </svg>
    ),
  },
  {
    id: 'real-estate-agent-bio',
    name: 'Agent Bio Page',
    category: 'About Pages',
    description: 'Professional bio to showcase your expertise',
    bg: 'linear-gradient(135deg, #f59e0b, #f97316)',
    source: 'local' as const,
    icon: (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  },
  {
    id: 'real-estate-contact',
    name: 'Contact Form Page',
    category: 'Contact Pages',
    description: 'Contact page with form and info',
    bg: 'linear-gradient(135deg, #6366f1, #3b82f6)',
    source: 'local' as const,
    icon: (
      <svg width="32" height="32" fill="none" stroke="white" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.9">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
];

interface AnyTemplate {
  id: string;
  name: string;
  category: string;
  description: string;
  badge?: string;
  bg: string;
  source: 'local' | 'backend';
  icon?: React.ReactNode;
}

const TemplateLibraryModal: React.FC<TemplateLibraryProps & { editor: any }> = ({ onClose, editor }) => {
  const [backendTemplates, setBackendTemplates] = useState<AnyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('All');

  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('jwt');
    fetch(`${API_BASE}/api/pages/templates/list`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.ok ? r.json() : [])
      .then((list: any[]) => {
        const mapped: AnyTemplate[] = list.map((t: any) => ({
          id: t.id,
          name: t.name,
          category: t.category,
          description: t.description || '',
          bg: BACKEND_STYLES[t.id]?.bg ?? 'linear-gradient(135deg, #64748b, #94a3b8)',
          source: 'backend',
        }));
        setBackendTemplates(mapped);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const allTemplates: AnyTemplate[] = [
    ...LOCAL_TEMPLATES,
    ...backendTemplates,
  ];

  // Unique category list preserving order
  const categories = ['All', ...Array.from(new Set(allTemplates.map((t) => t.category)))];

  const visible = activeCategory === 'All'
    ? allTemplates
    : allTemplates.filter((t) => t.category === activeCategory);

  const loadTemplate = useCallback(async (template: AnyTemplate) => {
    if (applying) return;
    setApplying(template.id);
    try {
      if (template.source === 'backend') {
        const token = localStorage.getItem('token') || localStorage.getItem('jwt');
        const res = await fetch(`${API_BASE}/api/pages/templates/${template.id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        const data = await res.json();

        editor.DomComponents.clear();
        if (editor.CssComposer) editor.CssComposer.clear();

        if (data.components) {
          // GrapesJS component JSON format
          editor.setComponents(data.components);
          editor.setStyle(data.styles || []);
        } else {
          // Plain HTML / CSS format
          editor.setComponents(data.html || '');
          editor.setStyle(prepareTemplateCss(editor, data.css || ''));
        }
        // Applying a template only updates the live editor state — nothing
        // persists until autosave's stepsBeforeSave threshold is hit, which
        // may never happen if the agent navigates away first. Save
        // immediately so the template survives the next session's load().
        await editor.store();
        onClose();
        return;
      }

      // Local template — load from customTemplates.js
      const module = await import('../data/customTemplates');
      const templates = module.customTemplates || module.default;
      const found = templates.find((t: any) => t.id === template.id);
      if (!found) { alert('Template not found.'); return; }

      if (found.pages?.length > 0) {
        const pageManager = editor.Pages;
        pageManager.getAll().slice(1).forEach((page: any) => {
          try { pageManager.remove(page.id || page); } catch {}
        });
        found.pages.forEach((pageData: any, index: number) => {
          if (index === 0) {
            const main = pageManager.getMain();
            if (main) {
              main.setName(pageData.name);
              pageManager.select(main);
              editor.setComponents(pageData.html || pageData.component || '');
              editor.setStyle(prepareTemplateCss(editor, pageData.css || pageData.styles || ''));
            }
          } else {
            try {
              pageManager.add({
                name: pageData.name,
                component: pageData.html || pageData.component || '',
                styles: pageData.css || pageData.styles || '',
              });
            } catch {}
          }
        });
        // editor.getStyle() returns a CssRules collection object, not a
        // string — concatenating it with '+' stringifies it to garbage
        // (e.g. "[object Object]"), which setStyle() then can't parse
        // correctly, silently dropping the real CSS that follows it
        // (including this template's own globalCss). getCss() is the text
        // form. Confirmed as the cause of the DreamX 7-page template
        // rendering with no styling applied at all.
        if (found.globalCss) {
          editor.setStyle(`${editor.getCss() || ''}\n${prepareTemplateCss(editor, found.globalCss)}`);
        }
        pageManager.select(pageManager.getMain());
      } else {
        editor.setComponents(found.html);
        if (found.css) editor.setStyle(prepareTemplateCss(editor, found.css));
      }
      // Applying a template only updates the live editor state — nothing
      // persists until autosave's stepsBeforeSave threshold is hit, which
      // may never happen if the agent navigates away first. Save
      // immediately so the template survives the next session's load().
      await editor.store();
      onClose();
    } catch (err) {
      console.error('Error loading template:', err);
      alert('Error loading template. Please try again.');
    } finally {
      setApplying(null);
    }
  }, [applying, editor, onClose]);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(3px)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div style={{
        background: '#17171a', borderRadius: 12,
        border: '1px solid #28282f',
        boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
        width: '100%', maxWidth: 900,
        maxHeight: 'calc(100vh - 60px)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid #1f1f25', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#ededf2' }}>Template Library</div>
            <div style={{ fontSize: 12, color: '#a3a3ad', marginTop: 2 }}>Choose a starting point</div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#a3a3ad', lineHeight: 0 }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#1d1d22')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Category filter */}
        <div style={{
          display: 'flex', gap: 6, padding: '12px 20px', overflowX: 'auto',
          borderBottom: '1px solid #1f1f25', flexShrink: 0, minWidth: 0,
        }}>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500,
                cursor: 'pointer', whiteSpace: 'nowrap', border: 'none', flexShrink: 0,
                background: activeCategory === cat ? '#3b82f6' : '#1d1d22',
                color: activeCategory === cat ? '#fff' : '#a3a3ad',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              {CATEGORY_LABELS[cat] ?? cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div style={{ overflowY: 'auto', padding: 16, flex: 1 }}>
          {loading && backendTemplates.length === 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={{ borderRadius: 10, overflow: 'hidden', border: '1.5px solid #28282f' }}>
                  <div style={{ height: 90, background: '#1d1d22', animation: 'pulse 1.5s infinite' }} />
                  <div style={{ padding: '10px 12px' }}>
                    <div style={{ height: 10, width: '60%', background: '#28282f', borderRadius: 4, marginBottom: 6 }} />
                    <div style={{ height: 12, width: '80%', background: '#1d1d22', borderRadius: 4 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              {visible.map((t) => {
                const isApplying = applying === t.id;
                return (
                  <button
                    key={`${t.source}-${t.id}`}
                    onClick={() => loadTemplate(t)}
                    disabled={!!applying}
                    style={{
                      background: '#1d1d22', border: '1.5px solid #28282f',
                      borderRadius: 10, overflow: 'hidden', cursor: applying ? 'wait' : 'pointer',
                      textAlign: 'left', padding: 0,
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                      opacity: applying && !isApplying ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!applying) {
                        e.currentTarget.style.borderColor = '#3b82f6';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(59,130,246,0.25)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#28282f';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {/* Thumbnail */}
                    <div style={{
                      height: 90, background: t.bg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
                    }}>
                      {t.badge && (
                        <span style={{
                          position: 'absolute', top: 8, right: 8,
                          background: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(4px)',
                          color: '#fff', fontSize: 10, fontWeight: 600,
                          padding: '2px 8px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.35)',
                        }}>
                          {t.badge}
                        </span>
                      )}
                      {isApplying ? (
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                        </svg>
                      ) : t.icon ? (
                        t.icon
                      ) : (
                        <TemplateIcon id={t.id} />
                      )}
                    </div>
                    {/* Info */}
                    <div style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: '#6b6b76', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {CATEGORY_LABELS[t.category] ?? t.category}
                        </span>
                        {t.source === 'backend' && (
                          <span style={{ fontSize: 9, fontWeight: 600, color: '#3b82f6', background: 'rgba(59,130,246,.14)', padding: '1px 5px', borderRadius: 4 }}>
                            SERVER
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#ededf2', lineHeight: 1.3 }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: '#a3a3ad', marginTop: 4, lineHeight: 1.4 }}>{t.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
      `}</style>
    </div>
  );
};

const TemplateLibrary: React.FC<TemplateLibraryProps> = ({ isOpen, onClose }) => {
  const editor = useEditor();
  if (!isOpen) return null;
  return ReactDOM.createPortal(
    <TemplateLibraryModal isOpen={isOpen} onClose={onClose} editor={editor} />,
    document.body
  );
};

export default TemplateLibrary;
